import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { useToast } from '../hooks/use-toast';
import { Lock, Shield } from 'lucide-react';
import api from '../utils/api';

const AdminLogin = () => {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const { toast } = useToast();
  const [companyInfo, setCompanyInfo] = useState(null);

  useEffect(() => {
    api.get('/company-info').then((r) => setCompanyInfo(r.data)).catch(() => {});
  }, []);

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const response = await api.post('/auth/login', {
        username,
        password
      });

      // トークンとユーザー情報を保存
      localStorage.setItem('token', response.data.token);
      localStorage.setItem('user', JSON.stringify(response.data.user));

      toast({
        title: 'ログイン成功',
        description: '管理画面へようこそ',
      });

      navigate('/admin/dashboard');
    } catch (error) {
      toast({
        title: 'ログイン失敗',
        description: error.response?.data?.detail || 'ユーザー名またはパスワードが正しくありません',
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-white flex items-center justify-center px-4 relative overflow-hidden">
      <Card className="w-full max-w-md relative z-10 bg-white border-amber-500/30 shadow-2xl shadow-amber-500/10">
        <CardHeader className="text-center space-y-4">
          <div className="flex flex-col items-center mb-4 space-y-2">
            <img 
              src={companyInfo?.logo_url || "https://customer-assets.emergentagent.com/job_presage-proposal/artifacts/gcigf741_Presage-Corporation-150x150.png"} 
              alt="Presage Corporation" 
              className="h-16 w-auto"
              data-testid="login-logo-img"
            />
            <div className="text-center">
              <div className="text-xl font-bold bg-gradient-to-r from-amber-500 to-amber-600 bg-clip-text text-transparent">
                Presage Corporation
              </div>
              <div className="text-xs text-gray-600 mt-1">株式会社プレサージュ</div>
            </div>
          </div>
          <div className="inline-flex items-center justify-center space-x-2 px-4 py-2 bg-amber-500/10 border border-amber-500/30 rounded-full mx-auto">
            <Shield className="w-4 h-4 text-amber-500" />
            <span className="text-sm text-amber-500">セキュアログイン</span>
          </div>
          <CardTitle className="text-3xl text-gray-900">管理画面</CardTitle>
          <CardDescription className="text-gray-600">管理者アカウントでログインしてください</CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleLogin} className="space-y-6">
            <div className="space-y-2">
              <Label htmlFor="username" className="text-gray-600">ユーザー名</Label>
              <Input
                id="username"
                type="text"
                placeholder=""
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                className="bg-white/50 border-amber-500/30 text-gray-900 placeholder:text-gray-500 focus:border-amber-500 focus:ring-amber-500/30"
                required
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="password" className="text-gray-600">パスワード</Label>
              <Input
                id="password"
                type="password"
                placeholder=""
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="bg-white/50 border-amber-500/30 text-gray-900 placeholder:text-gray-500 focus:border-amber-500 focus:ring-amber-500/30"
                required
              />
            </div>
            <Button 
              type="submit" 
              className="w-full bg-gradient-to-r from-amber-500 to-amber-600 hover:from-amber-600 hover:to-amber-700 text-gray-900 shadow-lg shadow-amber-500/50 hover:shadow-amber-500/70 transition-all duration-300" 
              disabled={loading}
            >
              {loading ? (
                <span className="flex items-center justify-center">
                  <span className="mr-2">ログイン中...</span>
                </span>
              ) : (
                <span className="flex items-center justify-center">
                  <Lock className="w-4 h-4 mr-2" />
                  ログイン
                </span>
              )}
            </Button>
          </form>
        </CardContent>
      </Card>
    </div>
  );
};

export default AdminLogin;
